import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Home,
  FolderKanban,
  CheckSquare,
  Users,
  BarChart3,
  Calendar,
  Clock,
  Layers,
  Milestone,
  Settings,
  Building2,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  LogOut,
  ClipboardList,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigationPreferences } from '../hooks/useNavigationPreferences';
import { fetchNotificationCounts, NotificationCounts } from '../services/notificationService';
import { hasAnyAdminAccess } from '../config/adminNav';
import { NavItem } from './NavItem';
import TenantSwitcher from './TenantSwitcher';

interface SidebarProps {
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ className = '' }) => {
  const { isAuthenticated, user, logout, isTenantAdmin, isPlatformAdmin } = useAuth();
  const { theme, effectiveTheme, setTheme } = useTheme();
  const { preferences, toggleSidebar } = useNavigationPreferences();
  const [notifications, setNotifications] = useState<NotificationCounts>({
    tasks: 0,
    overdue_tasks: 0,
    unassigned_tasks: 0,
    pending_registrations: 0,
    total: 0,
  });

  const collapsed = preferences.sidebarCollapsed;

  // Fetch notifications on mount and every 60 seconds
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadNotifications = async () => {
      const counts = await fetchNotificationCounts();
      setNotifications(counts);
    };

    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout();
  };

  const cycleTheme = () => {
    if (theme === 'light') setTheme('dark');
    else if (theme === 'dark') setTheme('system');
    else setTheme('light');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <aside
      className={`
        fixed left-0 top-0 bottom-0 z-40
        bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl
        border-r border-gray-200 dark:border-gray-700
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-16' : 'w-64'}
        ${className}
        hidden md:block
      `}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700`}>
          {!collapsed ? (
            <Link to="/" className="flex items-center gap-2">
              <ClipboardList className="text-primary-600 dark:text-primary-400" size={24} />
              <div className="flex flex-col">
                <span className="text-base font-bold bg-gradient-to-r from-primary-600 to-secondary-600 dark:from-primary-400 dark:to-secondary-400 bg-clip-text text-transparent">
                  Project Manager
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                  Powered by Akhile
                </span>
              </div>
            </Link>
          ) : (
            <Link to="/" className="flex items-center">
              <ClipboardList className="text-primary-600 dark:text-primary-400" size={24} />
            </Link>
          )}
          {/* Collapse toggle in header */}
          <button
            onClick={toggleSidebar}
            className={`
              flex items-center justify-center rounded-lg p-1.5
              text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800
              transition-colors duration-200
            `}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {/* Dashboard */}
          <NavItem to="/" icon={Home} label="Dashboard" collapsed={collapsed} />

          {/* Portfolio section */}
          {!collapsed && (
            <div className="pt-6">
              <div className="px-3 py-1.5 mb-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Portfolio
                </p>
              </div>
            </div>
          )}
          {collapsed && <div className="h-px bg-gray-200 dark:bg-gray-700 my-3" />}
          <NavItem to="/programmes" icon={Layers} label="Programmes" collapsed={collapsed} />
          <NavItem to="/projects" icon={FolderKanban} label="Projects" collapsed={collapsed} />
          <NavItem to="/milestones" icon={Milestone} label="Milestones" collapsed={collapsed} />

          {/* Execution section */}
          {!collapsed && (
            <div className="pt-6">
              <div className="px-3 py-1.5 mb-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Execution
                </p>
              </div>
            </div>
          )}
          {collapsed && <div className="h-px bg-gray-200 dark:bg-gray-700 my-3" />}
          <NavItem 
            to="/tasks" 
            icon={CheckSquare} 
            label="Tasks" 
            badge={notifications.tasks} 
            warningBadge={notifications.overdue_tasks}
            collapsed={collapsed} 
          />
          <NavItem to="/users" icon={Users} label="Team" collapsed={collapsed} />

          {/* Time & Planning section */}
          {!collapsed && (
            <div className="pt-6">
              <div className="px-3 py-1.5 mb-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Time & Planning
                </p>
              </div>
            </div>
          )}
          {collapsed && <div className="h-px bg-gray-200 dark:bg-gray-700 my-3" />}
          <NavItem to="/weekly-planner" icon={Calendar} label="Weekly Planner" collapsed={collapsed} />
          <NavItem to="/timesheets" icon={Clock} label="Timesheets" collapsed={collapsed} />

          {/* Reports */}
          {!collapsed && (
            <div className="pt-6">
              <div className="h-px bg-gray-200 dark:bg-gray-700 mb-3" />
            </div>
          )}
          {collapsed && <div className="h-px bg-gray-200 dark:bg-gray-700 my-3" />}
          <NavItem to="/reports" icon={BarChart3} label="Reports" collapsed={collapsed} />
          <NavItem to="/weekly-report" icon={ClipboardList} label="Weekly Report" collapsed={collapsed} />

          {/* Admin section. Both tiers get in via the hub, which shows each of them only the
              destinations their tier can actually open. */}
          {hasAnyAdminAccess({ isTenantAdmin, isPlatformAdmin }) && (
            <>
              {!collapsed && (
                <div className="pt-6">
                  <div className="px-3 py-1.5 mb-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Admin
                    </p>
                  </div>
                </div>
              )}
              {collapsed && <div className="h-px bg-gray-200 dark:bg-gray-700 my-3" />}
              {/* Approving registrations is tenant-tier work, so the badge count only means
                  something to a workspace administrator. */}
              <NavItem
                to="/admin/settings"
                icon={Settings}
                label="Settings"
                badge={isTenantAdmin ? notifications.pending_registrations : 0}
                collapsed={collapsed}
              />
              {/* Akhile's own back office, straight from the sidebar — it is the page they
                  live in when onboarding a client. */}
              {isPlatformAdmin && (
                <NavItem
                  to="/admin/tenants"
                  icon={Building2}
                  label="Workspaces"
                  collapsed={collapsed}
                />
              )}
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
          {/* Workspace switcher — renders itself away unless this login has more than one
              active membership. Expanded sidebar only: collapsed leaves 4rem, too narrow to
              show which workspace you are in, and showing the control without the name is
              worse than not showing it. */}
          {!collapsed && <TenantSwitcher />}

          {/* Theme toggle */}
          <button
            onClick={cycleTheme}
            className={`
              flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
              text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800
              transition-colors duration-200
              ${collapsed ? 'justify-center' : ''}
            `}
            title={`Theme: ${theme === 'system' ? 'System (' + effectiveTheme + ')' : theme}`}
          >
            {effectiveTheme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
            {!collapsed && (
              <span className="flex-1 text-left text-sm font-medium capitalize">
                {theme}
              </span>
            )}
          </button>

          {/* User info */}
          {!collapsed && (
            <div className="px-3 py-2 text-sm">
              <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {user?.full_name || user?.username}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.email}
              </p>
            </div>
          )}

          {/* Logout button */}
          <button
            onClick={handleLogout}
            className={`
              flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
              text-error hover:bg-error/10 dark:hover:bg-error/20
              transition-colors duration-200
              ${collapsed ? 'justify-center' : ''}
            `}
            title="Logout"
          >
            <LogOut size={20} />
            {!collapsed && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </div>
    </aside>
  );
};
